/**
 * @fileoverview Turns the shadow model's dirty sets into a handful of batched PyMOL calls.
 *
 * The renderer owns the PyMOL OBJECTS: which shadow atoms live in which object, what has to
 * be (re)loaded, removed or deleted, and what PyMOL was last told about each atom (its
 * "pushed" state). Nothing in here talks to PyMOL until flush(viewer) is called, and
 * everything before that works without a viewer - the visualizer buffers unconditionally.
 *
 * Cost model that shapes this file (pymol-web docs/phase1/wrapper.md section 9): changing one
 * atom rebuilds that representation for its WHOLE object (10 atoms on a 9.5k-atom object
 * still cost an 85 ms frame), a bridge call costs microseconds. So: many small objects, few
 * calls per object - one apply_reps per distinct style, one apply_colors per object.
 *
 * @typedef {{
 *   id: number, appAtom: object, objectKey: string, objectKind: string, residueKey: string|null,
 *   style: string, colorOverride: number|null, selected: boolean,
 *   pushedStyle: string|null, pushedColor: number|null,
 *   pushedSettings: Object<string, number>, pushedCartoonType: string,
 *   pushedCartoonColor: number|null,
 * }} VisAtom
 */

import { guarded } from './PyMOLGuard';
import {
    ObjectPrefix, ScratchSelection, RepOrder, ManagedReps, RepSetManagedReps, repMask, getStyleSpec,
    SettingDefaults, DefaultCartoonType,
} from './PyMOLConstants';
import { MolStyles, isRepSetStyle } from '../style_manager';
import { cifFromAtoms, makeSecondaryStructureLookup } from './PyMOLCifWriter';

/** Batch key suffix for atoms that leave a rep set (they get its wider managed mask). */
const LeavingRepSet = '\u0000leaving-rep-set';

function newStats() {
    return {
        deleted: 0,
        loaded: 0,
        removeCalls: 0,
        repCalls: 0,
        settingCalls: 0,
        colorCalls: 0,
        droppedBonds: 0,
        skippedAtoms: 0,
        errors: 0,
    };
}

export class PyMOLAtomRenderer {
    /**
     * @param {object} options
     * @param {(visAtom: VisAtom) => number} options.resolveColor effective 0xRRGGBB of an atom
     * @param {(visAtom: VisAtom) => number} [options.resolveCartoonColor] 0xRRGGBB of the cartoon /
     *     ribbon of an atom whose style is `cartoonColored` (a rep set mixing them with atom reps)
     * @param {(appAtom: object) => (VisAtom|undefined)} options.lookupVisAtom
     */
    constructor({
        resolveColor, resolveCartoonColor, lookupVisAtom, onObjectGivenUp,
    }) {
        this.resolveColor = resolveColor;
        this.resolveCartoonColor = resolveCartoonColor || resolveColor;
        this.lookupVisAtom = lookupVisAtom;
        /** Called with the record of an object given up after repeated load failures. */
        this.onObjectGivenUp = onObjectGivenUp;
        /** @type {Map<string, object>} object key -> object record */
        this.objects = new Map();
        /** Names of PyMOL objects to delete at the next flush. */
        this.deadObjectNames = [];
        this.objectSeq = 0;
        this.repOrder = RepOrder;
        this.managedMask = repMask(ManagedReps, this.repOrder);
        this.repSetManagedMask = repMask(RepSetManagedReps, this.repOrder);
        /** True when the set of pickable / hoverable atoms may have changed since a flush. */
        this.hoverStale = false;
        this.lastStats = newStats();
    }

    /** Use the loaded wrapper's own rep bit order (PyMolViewer.REPS). */
    setRepOrder(order) {
        if (!Array.isArray(order) || RepSetManagedReps.some((rep) => !order.includes(rep))) return;
        this.repOrder = order;
        this.managedMask = repMask(ManagedReps, order);
        this.repSetManagedMask = repMask(RepSetManagedReps, order);
    }

    // ------------------------------------------------------------------ shadow bookkeeping

    getObject(key) { return this.objects.get(key); }

    /** @returns {string|null} the PyMOL object name an atom lives in */
    objectNameFor(visAtom) {
        const obj = this.objects.get(visAtom.objectKey);
        return obj ? obj.name : null;
    }

    isManagedObjectName(name) {
        return typeof name === 'string' && name.startsWith(ObjectPrefix);
    }

    /** Names of the loaded objects holding at least one atom of `style` (e.g. `invisible`). */
    loadedObjectsWithStyle(style) {
        const names = [];
        for (const obj of this.objects.values()) {
            if (!obj.loaded) continue;
            for (const visAtom of obj.atoms.values()) {
                if (visAtom.style === style) {
                    names.push(obj.name);
                    break;
                }
            }
        }
        return names;
    }

    /** Names of the objects that currently exist in PyMOL. */
    loadedObjectNames() {
        const names = [];
        for (const obj of this.objects.values()) {
            if (obj.loaded) names.push(obj.name);
        }
        return names;
    }

    /** True while atoms are waiting to be loaded into (or removed from) PyMOL. */
    hasPendingStructure() {
        if (this.deadObjectNames.length > 0) return true;
        for (const obj of this.objects.values()) {
            if (obj.needsLoad || obj.removedIds.length > 0) return true;
        }
        return false;
    }

    addAtom(visAtom) {
        let obj = this.objects.get(visAtom.objectKey);
        if (!obj) {
            this.objectSeq++;
            obj = {
                key: visAtom.objectKey,
                kind: visAtom.objectKind,
                name: `${ObjectPrefix}${visAtom.objectKind}${this.objectSeq}`,
                /** @type {Map<number, VisAtom>} in load order */
                atoms: new Map(),
                loaded: false,
                needsLoad: true,
                removedIds: [],
                dirtyStyle: new Set(),
                dirtyColor: new Set(),
            };
            this.objects.set(obj.key, obj);
        }
        obj.atoms.set(visAtom.id, visAtom);
        // Appending = reload the object with all of its atoms (NOTES.md "Incremental adds").
        obj.needsLoad = true;
        return obj;
    }

    removeAtom(visAtom) {
        const obj = this.objects.get(visAtom.objectKey);
        if (!obj || !obj.atoms.delete(visAtom.id)) return;
        obj.dirtyStyle.delete(visAtom);
        obj.dirtyColor.delete(visAtom);
        if (obj.loaded && !obj.needsLoad) obj.removedIds.push(visAtom.id);
        if (obj.atoms.size === 0) {
            if (obj.loaded) this.deadObjectNames.push(obj.name);
            this.objects.delete(obj.key);
        }
    }

    markStyleDirty(visAtom) {
        const obj = this.objects.get(visAtom.objectKey);
        if (obj) obj.dirtyStyle.add(visAtom);
    }

    markColorDirty(visAtom) {
        const obj = this.objects.get(visAtom.objectKey);
        if (obj) obj.dirtyColor.add(visAtom);
    }

    /** Forget every object; the ones PyMOL holds are deleted at the next flush. */
    resetAll() {
        for (const obj of this.objects.values()) {
            if (obj.loaded) this.deadObjectNames.push(obj.name);
        }
        this.objects.clear();
    }

    /** PyMOL was emptied behind our back (`delete all`): nothing is left to delete. */
    forgetDeadObjects() {
        this.deadObjectNames = [];
        this.hoverStale = true;
    }

    // ------------------------------------------------------------------ flush

    /**
     * Push everything that changed since the last flush. Synchronous.
     * A failure in one object is logged and does not stop the others: Redisplay() is called
     * from all over the app and must not throw.
     * @param {object} viewer PyMolViewer (or a test double)
     * @returns {object} counters, see newStats()
     */
    flush(viewer) {
        const stats = newStats();
        this.lastStats = stats;

        for (const name of this.deadObjectNames) {
            this.guard(stats, `delete ${name}`, () => { viewer.delete(name); stats.deleted++; });
            this.hoverStale = true;
        }
        this.deadObjectNames = [];

        for (const obj of [...this.objects.values()]) {
            if (obj.needsLoad) {
                const ok = this.guard(stats, `load ${obj.name}`, () => this.loadObject(viewer, obj, stats));
                if (!ok) this.noteLoadFailure(obj);
            } else if (obj.removedIds.length > 0) {
                this.guard(stats, `remove from ${obj.name}`, () => {
                    const ids = obj.removedIds;
                    obj.removedIds = [];
                    this.withSelection(viewer, obj, ids, (sel) => viewer.cmd.remove(sel));
                    stats.removeCalls++;
                    this.hoverStale = true;
                });
            }
        }

        for (const obj of this.objects.values()) {
            if (!obj.loaded) continue;
            if (obj.dirtyStyle.size > 0) {
                this.guard(stats, `style ${obj.name}`, () => this.flushStyles(viewer, obj, stats));
            }
            if (obj.dirtyColor.size > 0) {
                this.guard(stats, `color ${obj.name}`, () => this.flushColors(viewer, obj, stats));
            }
        }
        return stats;
    }

    /** @returns {boolean} whether `fn` ran without throwing */
    guard(stats, what, fn) {
        return guarded(stats, what, fn);
    }

    /** Loads of one object that may throw before it is given up. */
    static get MaxLoadAttempts() { return 3; }

    /**
     * A load that threw keeps `needsLoad`, so the next flush tries again (a transient wrapper
     * error, a CIF PyMOL rejects once). After MaxLoadAttempts the object is given up: dropped
     * here and its atoms unmapped through onObjectGivenUp, so VisAtom() is null and the app
     * treats them as not drawn - rather than mapped, invisible, unpickable atoms whose dirty
     * sets grow at every restyle.
     */
    noteLoadFailure(obj) {
        obj.loadFailures = (obj.loadFailures || 0) + 1;
        if (obj.loadFailures < PyMOLAtomRenderer.MaxLoadAttempts) return;
        console.error(`PyMOL backend: giving up on ${obj.name} after ${obj.loadFailures} failed loads; its ${obj.atoms.size} atoms are not shown`);
        this.objects.delete(obj.key);
        if (obj.loaded) this.deadObjectNames.push(obj.name); // the delete itself was what failed
        if (this.onObjectGivenUp) this.onObjectGivenUp(obj);
    }

    /** Run `fn(selectionName)` with a scratch selection of `ids` inside one object. */
    withSelection(viewer, obj, ids, fn) {
        viewer.cmd.select_list(ScratchSelection, obj.name, Array.from(ids), { mode: 'id', quiet: 1 });
        try {
            return fn(ScratchSelection);
        } finally {
            viewer.cmd.delete(ScratchSelection);
        }
    }

    loadObject(viewer, obj, stats) {
        // needsLoad / removedIds are cleared only once PyMOL has the object: a load that throws
        // is retried at the next flush (bounded by noteLoadFailure), like a shape pool.
        if (obj.loaded) {
            viewer.delete(obj.name);
            obj.loaded = false;
        }
        const visAtoms = [...obj.atoms.values()];
        const idOf = (appAtom) => {
            const visAtom = this.lookupVisAtom(appAtom);
            return visAtom && visAtom.objectKey === obj.key ? visAtom.id : undefined;
        };
        const cif = cifFromAtoms(visAtoms.map((v) => v.appAtom), {
            idOf, name: obj.name, ssOf: makeSecondaryStructureLookup(),
        });
        stats.droppedBonds += cif.droppedBonds;
        stats.skippedAtoms += cif.skippedAtoms.length;
        if (cif.skippedAtoms.length > 0) {
            console.warn(`PyMOL backend: ${cif.skippedAtoms.length} atom(s) of ${obj.name} have no finite position and are not shown.`);
        }
        this.hoverStale = true;
        if (cif.atomCount === 0) {
            obj.needsLoad = false;
            obj.removedIds = [];
            return;
        }

        const count = viewer.bridge.load_cif(obj.name, cif.text, 0);
        if (typeof count === 'number' && count !== cif.atomCount) {
            console.warn(`PyMOL backend: ${obj.name} has ${count} atoms in PyMOL, expected ${cif.atomCount}.`);
        }
        // A block without a _pymol_bond loop makes PyMOL bond by distance (ions, hotspot atoms).
        if (cif.bondCount === 0 && cif.atomCount > 1) viewer.bridge.unbond_all(obj.name);
        // Start from "nothing shown" whatever the auto_show_* settings are.
        viewer.cmd.hide('everything', obj.name);
        obj.loaded = true;
        obj.needsLoad = false;
        obj.removedIds = [];
        obj.loadFailures = 0;
        stats.loaded++;

        for (const visAtom of visAtoms) {
            visAtom.pushedStyle = MolStyles.hidden;
            visAtom.pushedColor = null;
            visAtom.pushedSettings = {};
            visAtom.pushedCartoonType = DefaultCartoonType;
            visAtom.pushedCartoonColor = null;
            obj.dirtyStyle.add(visAtom);
            obj.dirtyColor.add(visAtom);
        }
    }

    flushStyles(viewer, obj, stats) {
        /** @type {Map<string, VisAtom[]>} batch key (the style, + LeavingRepSet) -> atoms */
        const byStyle = new Map();
        for (const visAtom of obj.dirtyStyle) {
            if (visAtom.style === visAtom.pushedStyle) continue;
            const key = visAtom.style + (isRepSetStyle(visAtom.pushedStyle) ? LeavingRepSet : '');
            if (!byStyle.has(key)) byStyle.set(key, []);
            byStyle.get(key).push(visAtom);
        }
        obj.dirtyStyle.clear();

        for (const [key, visAtoms] of byStyle) {
            const { style } = visAtoms[0];
            const spec = getStyleSpec(style) || getStyleSpec(MolStyles.sticks);
            const showMask = repMask(spec.reps, this.repOrder);
            // A rep set manages every rep it can show (its surface / mesh / dots are BMaps'), and
            // so does an atom leaving one; other atoms leave command-line overlays alone.
            const wide = !!spec.manages || key.endsWith(LeavingRepSet);
            const managed = wide
                ? (this.managedMask | this.repSetManagedMask) // eslint-disable-line no-bitwise
                : this.managedMask;
            const hideMask = managed & ~showMask; // eslint-disable-line no-bitwise
            const ids = Int32Array.from(visAtoms, (v) => v.id);
            viewer.bridge.apply_reps(obj.name, ids, showMask, hideMask);
            stats.repCalls++;
            for (const visAtom of visAtoms) visAtom.pushedStyle = style;

            // Settings: only the ones this style needs, only for atoms that do not have the
            // value yet. The common styles (sticks, cartoon, spacefill, wireframe) use PyMOL's
            // defaults, so they normally cost no call at all.
            for (const [name, value] of Object.entries(spec.settings || {})) {
                const stale = visAtoms.filter((v) => pushedSetting(v, name) !== value);
                if (stale.length === 0) continue;
                const staleIds = Int32Array.from(stale, (v) => v.id);
                viewer.bridge.set_atom_setting(obj.name, staleIds, name, value);
                stats.settingCalls++;
                for (const visAtom of stale) visAtom.pushedSettings[name] = value;
            }
            if (spec.cartoonType) {
                // putty scales by the spread of B-factors; without one it collapses, so a tube
                const type = spec.cartoonType === 'putty' && !hasBfactorSpread(obj)
                    ? 'tube' : spec.cartoonType;
                const stale = visAtoms.filter((v) => v.pushedCartoonType !== type);
                if (stale.length > 0) {
                    this.withSelection(viewer, obj, stale.map((v) => v.id),
                        (sel) => viewer.cmd.cartoon(type, sel));
                    stats.settingCalls++;
                    for (const visAtom of stale) visAtom.pushedCartoonType = type;
                }
            }
        }
        if (byStyle.size > 0) this.hoverStale = true;
    }

    flushColors(viewer, obj, stats) {
        const ids = [];
        const colors = [];
        const pushed = [];
        /** @type {Map<number|null, VisAtom[]>} cartoon color -> atoms (null = back to -1) */
        const cartoonColors = new Map();
        for (const visAtom of obj.dirtyColor) {
            const spec = getStyleSpec(visAtom.style);
            // An atom that shows nothing keeps its old color in PyMOL; StyleAtom marks the
            // color dirty again when it becomes visible. (`recolor` on hidden atoms would
            // still invalidate the object's representations.)
            if (spec && spec.reps.length === 0) continue;
            this.queueCartoonColor(visAtom, spec, cartoonColors);
            const color = this.resolveColor(visAtom);
            if (color === visAtom.pushedColor) continue;
            ids.push(visAtom.id);
            colors.push(color);
            pushed.push(visAtom);
        }
        obj.dirtyColor.clear();
        this.flushCartoonColors(viewer, obj, stats, cartoonColors);
        if (ids.length === 0) return;
        viewer.bridge.apply_colors(obj.name, Int32Array.from(ids), Uint32Array.from(colors));
        stats.colorCalls++;
        pushed.forEach((visAtom, i) => { visAtom.pushedColor = colors[i]; });
    }

    /**
     * cartoon_color / ribbon_color of an atom whose style is `cartoonColored` (a rep set mixing
     * cartoon or ribbon with atom reps): the atom keeps its own color, its cartoon gets the
     * per-residue one. Back to -1 (= the atom's color) when it no longer is. Queued per value.
     */
    queueCartoonColor(visAtom, spec, queue) {
        const want = spec && spec.cartoonColored ? this.resolveCartoonColor(visAtom) : null;
        const pushed = visAtom.pushedCartoonColor === undefined ? null : visAtom.pushedCartoonColor;
        if (want === pushed) return;
        if (!queue.has(want)) queue.set(want, []);
        queue.get(want).push(visAtom);
    }

    flushCartoonColors(viewer, obj, stats, queue) {
        for (const [color, visAtoms] of queue) {
            // PyMOL takes a color setting as a "0xRRGGBB" STRING; the same number as an int is read
            // as a color index and does nothing (checked on payload 0.1.2).
            const value = color === null ? -1 : `0x${color.toString(16).padStart(6, '0')}`;
            const ids = Int32Array.from(visAtoms, (v) => v.id);
            viewer.bridge.set_atom_setting(obj.name, ids, 'cartoon_color', value);
            viewer.bridge.set_atom_setting(obj.name, ids, 'ribbon_color', value);
            stats.settingCalls += 2;
            for (const visAtom of visAtoms) visAtom.pushedCartoonColor = color;
        }
    }
}

/**
 * Whether an object's atoms have B-factors that differ (the CIF writer sends a missing one as 0,
 * and bfd-server sends none without its BFactor protocol feature).
 */
function hasBfactorSpread(obj) {
    let first;
    for (const visAtom of obj.atoms.values()) {
        const b = visAtom.appAtom && visAtom.appAtom.Bfactor;
        if (Number.isFinite(b)) {
            if (first === undefined) first = b;
            else if (b !== first) return true;
        }
    }
    return false;
}

function pushedSetting(visAtom, name) {
    const value = visAtom.pushedSettings[name];
    return value === undefined ? SettingDefaults[name] : value;
}
